/*
 * The header (assets/header.js), one IIFE since TN-12: nothing here is a global except
 * `window.headerController`, which theme.js (Search) reads — `mobileMenu`, `closeMobileMenu()`,
 * `isMobileMenuOpen()`, `closeDesktopMenus()`, `toggleOverlay()`, `activeDesktopItem`.
 *
 * - NavDrawer (TN-12): the phone/tablet menu, a modal <dialog> outside <header>
 *   (snippets/nav-drawer.liquid) that slides in from the left; groups unfold in place, one at
 *   a time, with the tapped row held under the finger.
 * - ChipsPeek (TN-13): pads the chips so the first chip past the right edge always shows about
 *   half its width — the cue that the row scrolls.
 * - DesktopNav + NavGlide (TN-22): the desktop row's one morphing panel with hover intent and
 *   keyboard support, and the soft highlight that springs after the pointer.
 * - SlidingHeader (TN-23): the row (chips below `lg`) slides behind the navy bar on the way
 *   down and back on the first scroll up; the yellow bar scrolls away with the page.
 * - HeaderController: wires them and keeps the public API.
 * - Tracking (TN-07, NAV-19): `ew_nav_open` / `ew_nav_click` with item, level and device.
 */
(function () {
  const DESKTOP_QUERY = '(min-width: 990px)';
  const DRAWER_CLOSE_MS = 300;
  const GROUP_MS = 320;
  const HOLD_ROW_MS = 380;
  const CHIP_PAD = 14;

  /* ------------------------------------------------------------------ pure helpers */

  // The visible label of a nav control, for tracking: its text without decorative,
  // aria-hidden parts (the seasonal tree, icons), whitespace collapsed.
  function navLabel(element) {
    if (!element) return '';
    const copy = element.cloneNode(true);
    copy
      .querySelectorAll('[aria-hidden="true"]')
      .forEach((node) => node.remove());
    return (copy.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function navTrack(event, params) {
    if (!window.EwTrack) return;
    window.EwTrack.push(event, {
      ...params,
      device: window.EwTrack.device(),
    });
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // The accordion: one group open at a time. Tapping the open one folds it.
  // → {open: key or null, fold: [keys]}
  function accordion(openKey, tappedKey) {
    if (openKey === tappedKey) return { open: null, fold: [tappedKey] };
    return { open: tappedKey, fold: openKey ? [openKey] : [] };
  }

  // How far to scroll so the tapped row stays where the finger left it: its drift since the
  // tap, ignored under half a pixel.
  function holdCorrection(anchorTop, currentTop) {
    const drift = currentTop - anchorTop;
    return Math.abs(drift) > 0.5 ? drift : 0;
  }

  /*
   * The chip padding nudge, the lab's peek(): try every extra horizontal padding `e` from -3
   * to +7 px in quarter steps and keep the one that leaves the first chip crossing the right
   * edge closest to half visible. `widths` are the chips at the base padding.
   * → e in px (0 when nothing crosses the edge).
   */
  function choosePad(widths, rowWidth, paddingLeft, gap) {
    const visibleFraction = (e) => {
      let x = paddingLeft;
      for (let i = 0; i < widths.length; i++) {
        const w = widths[i] + 2 * e;
        if (x + w > rowWidth) return (rowWidth - x) / w;
        x += w + gap;
      }
      return -1;
    };
    let best = 0;
    let bestScore = Infinity;
    for (let step = -12; step <= 28; step++) {
      const e = step / 4;
      const fraction = visibleFraction(e);
      if (fraction < 0) continue;
      const score = Math.abs(fraction - 0.5);
      if (score < bestScore - 1e-6) {
        bestScore = score;
        best = e;
      }
    }
    return best;
  }

  /* -------------------------------------------------------------------- NavDrawer */

  class NavDrawer {
    constructor(dialog, controller) {
      // Out from under the header section, which is `position: sticky`. WebKit hit-tests a
      // top-layer dialog through its ancestor's sticky offset, so on a scrolled page every tap
      // on the open drawer (Close, the rows, the backdrop) fell through to the page and the
      // menu could be neither used nor closed. destroy() removes it again: the theme editor
      // re-renders the section with a fresh one.
      if (dialog.parentElement !== document.body)
        document.body.appendChild(dialog);
      this.dialog = dialog;
      this.controller = controller;
      this.scroller = dialog.querySelector('[data-nav-drawer-scroll]');
      this.opener = null;
      this.openGroup = null;
      this.locked = false;
      this.desktop = window.matchMedia(DESKTOP_QUERY);

      this.handleDesktop = this.handleDesktop.bind(this);
      this.handleClosed = this.handleClosed.bind(this);

      dialog
        .querySelector('[data-nav-drawer-close]')
        ?.addEventListener('click', () => this.close());
      // Esc: animate instead of the instant close.
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      // A second Esc can close a modal without a preventable `cancel` (Chrome's close
      // watcher), so the cleanup runs from `close` too, not only from the animated path.
      dialog.addEventListener('close', this.handleClosed);
      dialog.addEventListener('click', (event) => {
        // The backdrop: a click on the dialog box itself, outside its content.
        if (event.target === dialog) {
          this.close();
          return;
        }
        const toggle = event.target.closest('[data-nav-group-toggle]');
        if (toggle) {
          this.toggleGroup(toggle);
          return;
        }
        const link = event.target.closest('a[href]');
        if (link) {
          navTrack('ew_nav_click', {
            item: navLabel(link),
            level: link.getAttribute('data-nav-level') || 'drawer',
          });
        }
      });
      this.desktop.addEventListener('change', this.handleDesktop);
    }

    isOpen() {
      return this.dialog.open;
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener || null;
      this.controller?.closeDesktopMenus();
      if (window.searchInstance) {
        window.searchInstance._close('desktop');
        window.searchInstance._close('mobile');
      }
      this.dialog.removeAttribute('data-closing');
      this.dialog.showModal();
      // The drawer takes focus, not its Close button: a phone draws the focus ring round
      // whatever showModal() focuses, even on a tap, and the x looked selected. Tab still
      // reaches it, with its ring; Escape still closes.
      this.dialog.focus({ preventScroll: true });
      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      // On an iPhone the overflow alone does not hold the page: assets/ew-touch-guard.js
      // refuses the swipes that would move it while the drawer is open.
      this.locked = true;
      this.opener?.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (!this.dialog.open || this.dialog.hasAttribute('data-closing')) return;
      const finish = () => {
        if (this.dialog.open) this.dialog.close();
        this.handleClosed();
      };
      if (reducedMotion()) {
        finish();
        return;
      }
      this.dialog.setAttribute('data-closing', '');
      window.setTimeout(finish, DRAWER_CLOSE_MS);
    }

    // Idempotent: from the close timer and from the `close` event, whichever comes first.
    handleClosed() {
      this.dialog.removeAttribute('data-closing');
      if (!this.locked) return;
      this.locked = false;
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
      this.opener?.setAttribute('aria-expanded', 'false');
      // Focus goes back through the dialog's own close(), to whatever held it before
      // showModal(). Focusing Menu from here drew the iPhone's focus ring round it after
      // every tap close, because a tap never focuses a button there.
      this.collapseAll();
    }

    handleDesktop(event) {
      if (event.matches && this.dialog.open) this.close();
    }

    groupFor(toggle) {
      return document.getElementById(toggle.getAttribute('aria-controls'));
    }

    collapseAll() {
      this.dialog
        .querySelectorAll('[data-nav-group-toggle][aria-expanded="true"]')
        .forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
          const group = this.groupFor(toggle);
          if (!group) return;
          group.hidden = true;
          group.style.height = '';
          group.classList.remove('is-open');
        });
      this.openGroup = null;
    }

    toggleGroup(toggle) {
      const key = toggle.getAttribute('aria-controls');
      const step = accordion(this.openGroup, key);
      const anchorTop = toggle.getBoundingClientRect().top;

      step.fold.forEach((foldKey) => {
        const other = this.dialog.querySelector(
          `[data-nav-group-toggle][aria-controls="${foldKey}"]`,
        );
        if (!other) return;
        other.setAttribute('aria-expanded', 'false');
        this.fold(this.groupFor(other));
      });

      if (step.open) {
        toggle.setAttribute('aria-expanded', 'true');
        const group = this.groupFor(toggle);
        this.controller?.warmPanelImages(group);
        this.unfold(group);
        navTrack('ew_nav_open', { item: navLabel(toggle), level: 'drawer' });
      }
      this.openGroup = step.open;
      this.holdRow(toggle, anchorTop);
    }

    unfold(group) {
      if (!group) return;
      window.clearTimeout(group._navTimer);
      group.hidden = false;
      if (reducedMotion()) {
        group.style.height = '';
        group.classList.add('is-open');
        return;
      }
      group.style.height = '0px';
      void group.offsetHeight;
      group.classList.add('is-open');
      group.style.height = `${group.scrollHeight}px`;
      group._navTimer = window.setTimeout(() => {
        if (group.classList.contains('is-open')) group.style.height = '';
      }, GROUP_MS);
    }

    fold(group) {
      if (!group || group.hidden) return;
      window.clearTimeout(group._navTimer);
      if (reducedMotion()) {
        group.classList.remove('is-open');
        group.hidden = true;
        group.style.height = '';
        return;
      }
      group.style.height = `${group.offsetHeight}px`;
      void group.offsetHeight;
      group.classList.remove('is-open');
      group.style.height = '0px';
      group._navTimer = window.setTimeout(() => {
        if (group.classList.contains('is-open')) return;
        group.hidden = true;
        group.style.height = '';
      }, GROUP_MS);
    }

    // For the length of the unfold, scroll by whatever the tapped row drifted, so it stays
    // under the finger while a group above it folds. Once under reduced motion.
    holdRow(toggle, anchorTop) {
      if (!this.scroller) return;
      const started = performance.now();
      const step = () => {
        const delta = holdCorrection(
          anchorTop,
          toggle.getBoundingClientRect().top,
        );
        if (delta) this.scroller.scrollTop += delta;
        if (!reducedMotion() && performance.now() - started < HOLD_ROW_MS) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }

    destroy() {
      this.desktop.removeEventListener('change', this.handleDesktop);
      if (this.dialog.open) {
        this.dialog.close();
        this.handleClosed();
      }
      this.dialog.remove();
    }
  }

  /* -------------------------------------------------------------------- ChipsPeek */

  class ChipsPeek {
    constructor(row) {
      this.row = row;
      this.frame = null;
      this.schedule = this.schedule.bind(this);
      if (typeof ResizeObserver === 'function') {
        this.observer = new ResizeObserver(this.schedule);
        this.observer.observe(row);
      }
      document.fonts?.ready?.then(this.schedule);
      row.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (link)
          navTrack('ew_nav_click', { item: navLabel(link), level: 'chip' });
      });
      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    }

    measure() {
      const row = this.row;
      if (!row.clientWidth) return;
      row.style.setProperty('--chip-pad', `${CHIP_PAD}px`);
      const chips = [...row.children];
      if (!chips.length) return;
      const style = getComputedStyle(row);
      const widths = chips.map((chip) => chip.getBoundingClientRect().width);
      const extra = choosePad(
        widths,
        row.clientWidth,
        parseFloat(style.paddingLeft) || 0,
        parseFloat(style.columnGap) || 8,
      );
      row.style.setProperty('--chip-pad', `${CHIP_PAD + extra}px`);
    }

    destroy() {
      this.observer?.disconnect();
    }
  }

  /*
   * The gliding highlight's spring, pure (NAV §6): each edge of the pill rides its own damped
   * spring; the edge in the direction of travel is stiffer (lead k=300, ζ=.8; trail k=180,
   * ζ=.88; c = 2√k·ζ), so the pill stretches toward the next item and settles. Six sub-steps
   * per frame; the caller caps dt at 34 ms. Opacity eases at 16/s.
   * → true once settled (< .5 px apart and < .5 px/s on both edges).
   */
  const GLIDE_LEAD = [300, 0.8];
  const GLIDE_TRAIL = [180, 0.88];

  function glideAdvance(g, dt) {
    const left = g.dir > 0 ? GLIDE_TRAIL : GLIDE_LEAD;
    const right = g.dir > 0 ? GLIDE_LEAD : GLIDE_TRAIL;
    const cL = 2 * Math.sqrt(left[0]) * left[1];
    const cR = 2 * Math.sqrt(right[0]) * right[1];
    const h = dt / 6;
    for (let i = 0; i < 6; i++) {
      g.vL += (-left[0] * (g.L - g.tL) - cL * g.vL) * h;
      g.L += g.vL * h;
      g.vR += (-right[0] * (g.R - g.tR) - cR * g.vR) * h;
      g.R += g.vR * h;
    }
    g.a += (g.ta - g.a) * (1 - Math.exp(-dt * 16));
    const settled =
      Math.abs(g.L - g.tL) < 0.5 &&
      Math.abs(g.R - g.tR) < 0.5 &&
      Math.abs(g.vL) < 0.5 &&
      Math.abs(g.vR) < 0.5 &&
      Math.abs(g.a - g.ta) < 0.01;
    if (settled) {
      g.L = g.tL;
      g.R = g.tR;
      g.vL = 0;
      g.vR = 0;
      g.a = g.ta;
    }
    return settled;
  }

  /*
   * Hover intent, pure: what a pointer entering a trigger schedules.
   * → {delay} in ms: 300 to open from closed, 110 to switch while a panel is open.
   */
  const HOVER_OPEN_MS = 300;
  const HOVER_SWITCH_MS = 110;
  const HOVER_CLOSE_MS = 380;
  const PANEL_CLOSE_MS = 230;
  const PANEL_SWAP_MS = 110;
  const PANEL_SETTLE_MS = 340;

  function hoverDelay(openKey) {
    return openKey ? HOVER_SWITCH_MS : HOVER_OPEN_MS;
  }

  /* ---------------------------------------------------------------------- NavGlide */

  class NavGlide {
    constructor(items, row) {
      this.items = items;
      this.row = row;
      this.el = items.querySelector('[data-nav-glide]');
      this.g = { L: 0, R: 0, vL: 0, vR: 0, tL: 0, tR: 0, a: 0, ta: 0, dir: 0 };
      this.hover = null;
      this.open = null;
      this.frame = 0;
      this.last = 0;
      this.step = this.step.bind(this);
      if (!this.el) return;
      row.classList.add('has-glide');

      items.addEventListener('mouseover', (event) => {
        const item = event.target.closest('.nav-it');
        if (item && item !== this.hover) {
          this.hover = item;
          this.sync();
        }
      });
      items.addEventListener('mouseleave', () => {
        this.hover = null;
        this.sync();
      });
      items.addEventListener('focusin', (event) => {
        const item = event.target.closest('.nav-it');
        if (item && event.target.matches(':focus-visible')) {
          this.hover = item;
          this.sync();
        }
      });
      items.addEventListener('focusout', (event) => {
        if (items.contains(event.relatedTarget)) return;
        this.hover = null;
        this.sync();
      });
    }

    setOpen(trigger) {
      this.open = trigger || null;
      this.sync();
    }

    // Where the pill should be: the hovered or focused item, else the open one, else the
    // current section's item, else nowhere (it fades out in place).
    target() {
      return (
        this.hover ||
        this.open ||
        this.items.querySelector('.nav-it[aria-current]') ||
        null
      );
    }

    sync(instant) {
      if (!this.el) return;
      const g = this.g;
      const t = this.target();
      if (t) {
        const L = t.offsetLeft;
        const R = L + t.offsetWidth;
        if (instant || reducedMotion() || g.a < 0.05) {
          g.L = L;
          g.R = R;
          g.vL = 0;
          g.vR = 0;
        } else if (L !== g.tL || R !== g.tR) {
          g.dir = L + R > g.L + g.R ? 1 : -1;
        }
        g.tL = L;
        g.tR = R;
        g.ta = 1;
      } else {
        g.ta = 0;
      }
      if (instant || reducedMotion()) {
        g.a = g.ta;
        g.L = g.tL;
        g.R = g.tR;
        this.paint();
        return;
      }
      if (!this.frame) {
        this.last = performance.now();
        this.frame = window.requestAnimationFrame(this.step);
      }
    }

    step(now) {
      const dt = Math.min(0.034, Math.max(0, (now - this.last) / 1000));
      this.last = now;
      const settled = glideAdvance(this.g, dt);
      this.paint();
      this.frame = settled ? 0 : window.requestAnimationFrame(this.step);
    }

    paint() {
      const g = this.g;
      const width = Math.max(0, g.R - g.L);
      const stretch = Math.max(0, width - (g.tR - g.tL));
      const scale = 0.9 + 0.1 * g.a;
      const squash = scale * (1 - Math.min(0.05, stretch / 900));
      this.el.style.width = `${width.toFixed(2)}px`;
      this.el.style.transform = `translateX(${g.L.toFixed(
        2,
      )}px) scale(${scale.toFixed(3)}, ${squash.toFixed(3)})`;
      this.el.style.opacity = g.a.toFixed(3);
    }

    destroy() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }
  }

  /* -------------------------------------------------------------------- DesktopNav */

  /*
   * The desktop row's one panel (TN-22, NAV-15). Hover opens after 300 ms, switches after
   * 110 ms while open, and a hover-opened panel closes 380 ms after the pointer leaves the
   * header; a click opens at once and toggles. Esc, a click outside, any scroll over 2 px, the
   * slim header and focus leaving the header close it. The panel morphs between contents:
   * the old fades out, the frame height glides from the old height to the new, the new rises in.
   * Every step is on a timer, never on transitionend.
   */
  class DesktopNav {
    constructor(header, navigation, controller) {
      this.header = header;
      this.row = navigation.querySelector('[data-nav-row]');
      this.panel = navigation.querySelector('[data-nav-panel]');
      this.frame = this.panel?.querySelector('[data-nav-panel-frame]');
      this.controller = controller;
      this.openKey = null;
      this.byHover = false;
      this.hoverTimer = null;
      this.leaveTimer = null;
      this.swapTimer = null;
      this.settleTimer = null;
      this.closeTimer = null;
      this.generation = 0;
      this.openedAt = 0;
      this.scrollAtOpen = 0;
      this.blocked = false;
      if (!this.row || !this.panel) return;

      this.triggers = [...this.row.querySelectorAll('[data-nav-trigger]')];
      const items = this.row.querySelector('[data-nav-items]');
      this.glide = items ? new NavGlide(items, this.row) : null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          this.clearHover();
          this.byHover = false;
          const key = trigger.getAttribute('data-nav-trigger');
          if (this.openKey === key) this.close();
          else this.open(key);
        });
        // Pointer events, so a tap on a touch laptop is a click, not a hover.
        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || this.blocked) return;
          const key = trigger.getAttribute('data-nav-trigger');
          window.clearTimeout(this.hoverTimer);
          this.hoverTimer = window.setTimeout(() => {
            if (this.openKey !== key && !this.blocked) {
              this.byHover = true;
              this.open(key);
            }
          }, hoverDelay(this.openKey));
        });
        trigger.addEventListener('pointerleave', () => {
          window.clearTimeout(this.hoverTimer);
        });
        trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'Enter') return;
          event.preventDefault();
          this.clearHover();
          this.byHover = false;
          this.open(trigger.getAttribute('data-nav-trigger'));
          this.links()[0]?.focus();
        });
      });

      // The pointer back anywhere in the header cancels a pending close; leaving it starts one.
      this.header.addEventListener('pointerenter', () =>
        window.clearTimeout(this.leaveTimer),
      );
      this.header.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        window.clearTimeout(this.hoverTimer);
        if (!this.byHover || !this.openKey) return;
        window.clearTimeout(this.leaveTimer);
        this.leaveTimer = window.setTimeout(() => {
          if (this.byHover) this.close();
        }, HOVER_CLOSE_MS);
      });

      this.panel.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const links = this.links();
        if (!links.length) return;
        const trigger = this.triggerFor(this.openKey);
        if (event.shiftKey && document.activeElement === links[0]) {
          event.preventDefault();
          trigger?.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === links[links.length - 1]
        ) {
          event.preventDefault();
          const next = this.nextFocusable(trigger);
          this.close();
          next?.focus();
        }
      });
      this.header.addEventListener('focusout', (event) => {
        if (!this.openKey) return;
        if (event.relatedTarget && this.header.contains(event.relatedTarget))
          return;
        if (!event.relatedTarget) return;
        this.close();
      });

      [this.row, this.panel].forEach((scope) =>
        scope.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (!link) return;
          navTrack('ew_nav_click', {
            item: navLabel(link),
            level: link.getAttribute('data-nav-level') || 'panel',
          });
        }),
      );
    }

    triggerFor(key) {
      return this.triggers.find(
        (trigger) => trigger.getAttribute('data-nav-trigger') === key,
      );
    }

    contentFor(key) {
      return this.panel.querySelector(`[data-nav-content="${key}"]`);
    }

    links() {
      const content = this.openKey && this.contentFor(this.openKey);
      return content ? [...content.querySelectorAll('a[href]')] : [];
    }

    // The item after this trigger in the row (the next trigger, a link, or the Redeem pill).
    nextFocusable(trigger) {
      const all = [...this.row.querySelectorAll('.nav-it, a[href]')];
      const index = all.indexOf(trigger);
      return index >= 0 ? all[index + 1] : null;
    }

    clearHover() {
      window.clearTimeout(this.hoverTimer);
      window.clearTimeout(this.leaveTimer);
    }

    // The slim header: panels close and cannot open (TN-23).
    setBlocked(blocked) {
      this.blocked = blocked;
      if (blocked) this.close();
    }

    open(key) {
      if (!key || this.blocked || this.openKey === key) return;
      const content = this.contentFor(key);
      const trigger = this.triggerFor(key);
      if (!content || !trigger) return;
      const wasOpen = !!this.openKey;
      const previous = wasOpen ? this.contentFor(this.openKey) : null;
      const generation = ++this.generation;

      window.clearTimeout(this.closeTimer);
      window.searchInstance?._close('desktop');
      this.controller?.warmPanelImages(content);
      this.openKey = key;
      this.openedAt = Date.now();
      this.scrollAtOpen = window.scrollY;
      this.triggers.forEach((item) =>
        item.setAttribute('aria-expanded', String(item === trigger)),
      );
      this.panel.setAttribute('aria-labelledby', trigger.id);
      document.documentElement.setAttribute('data-nav-panel-open', '');
      this.controller?.toggleOverlay(true);
      this.glide?.setOpen(trigger);
      navTrack('ew_nav_open', { item: navLabel(trigger), level: 'panel' });

      const calm = reducedMotion();
      if (!wasOpen || this.panel.classList.contains('is-closing')) {
        this.panel.querySelectorAll('[data-nav-content]').forEach((section) => {
          section.hidden = section !== content;
          section.classList.remove('is-in', 'is-out');
        });
        this.frame.style.height = '';
        this.panel.classList.remove('is-closing', 'hidden');
        if (calm) return;
        this.panel.classList.add('is-entering');
        void this.panel.offsetHeight;
        window.requestAnimationFrame(() =>
          this.panel.classList.remove('is-entering'),
        );
        return;
      }

      // Switch: fade the old out, then swap and glide the frame to the new height.
      const from = this.frame.offsetHeight;
      this.frame.style.height = `${from}px`;
      previous?.classList.add('is-out');
      window.clearTimeout(this.swapTimer);
      this.swapTimer = window.setTimeout(
        () => {
          if (generation !== this.generation) return;
          this.panel
            .querySelectorAll('[data-nav-content]')
            .forEach((section) => {
              section.hidden = section !== content;
              section.classList.remove('is-out');
            });
          if (!calm) content.classList.add('is-in');
          this.frame.style.height = 'auto';
          const to = this.frame.offsetHeight;
          this.frame.style.height = `${from}px`;
          void this.frame.offsetHeight;
          this.frame.style.height = `${to}px`;
          window.requestAnimationFrame(() => content.classList.remove('is-in'));
          window.clearTimeout(this.settleTimer);
          this.settleTimer = window.setTimeout(
            () => {
              if (generation === this.generation) this.frame.style.height = '';
            },
            calm ? 0 : PANEL_SETTLE_MS,
          );
        },
        calm ? 0 : PANEL_SWAP_MS,
      );
    }

    close() {
      if (!this.openKey) return;
      const trigger = this.triggerFor(this.openKey);
      this.openKey = null;
      this.byHover = false;
      this.clearHover();
      ++this.generation;
      this.triggers.forEach((item) =>
        item.setAttribute('aria-expanded', 'false'),
      );
      document.documentElement.removeAttribute('data-nav-panel-open');
      this.controller?.toggleOverlay(false);
      this.glide?.setOpen(null);
      const finish = () => {
        this.panel.classList.add('hidden');
        this.panel.classList.remove('is-closing');
        this.frame.style.height = '';
      };
      if (reducedMotion()) {
        finish();
        return trigger;
      }
      this.panel.classList.add('is-closing');
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(finish, PANEL_CLOSE_MS);
      return trigger;
    }

    activeTrigger() {
      return this.openKey ? this.triggerFor(this.openKey) : null;
    }

    onDocumentClick(event) {
      if (this.openKey && !this.header.contains(event.target)) this.close();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.clearHover();
      if (!this.openKey) return;
      const trigger = this.close();
      trigger?.focus();
    }

    // Any scroll over 2px closes the panel; the first half second after opening is ignored,
    // so an anchor jump or a scroll already in flight does not slam it shut.
    onScroll() {
      if (!this.openKey || Date.now() - this.openedAt < 500) return;
      if (Math.abs(window.scrollY - this.scrollAtOpen) > 2) this.close();
    }

    destroy() {
      this.close();
      this.glide?.destroy();
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  /* ------------------------------------------------------------------ SlidingHeader */

  /*
   * The sliding header (TN-23, NAV-17). The section wrapper is sticky (app.scss), so the
   * yellow bar scrolls away on its own; this only decides when the row (chips below `lg`)
   * slides behind the navy bar. Past 200px of scroll on desktop, 150px below `lg`, a 10px
   * run in one direction flips it; the run resets when the direction changes. Only the
   * `is-slim` class on the wrapper changes. It also publishes the heights CSS reads.
   */
  const SLIM_FROM_DESKTOP = 200;
  const SLIM_FROM_PHONE = 150;
  const SLIM_RUN = 10;

  // The slim decision, pure. → {slim, run}
  function slimStep(state, y, desktop) {
    const delta = y - state.y;
    let run = state.run;
    if ((delta > 0 && run < 0) || (delta < 0 && run > 0)) run = 0;
    run += delta;
    let slim = state.slim;
    if (y < (desktop ? SLIM_FROM_DESKTOP : SLIM_FROM_PHONE)) slim = false;
    else if (run > SLIM_RUN) slim = true;
    else if (run < -SLIM_RUN) slim = false;
    return { slim, run, y };
  }

  class SlidingHeader {
    constructor(header, onSlim) {
      this.header = header;
      this.wrapper = header.closest('.header-section') || header.parentElement;
      this.onSlim = onSlim;
      // Whichever top bar the header renders: the benefits bar or the seasonal bar that
      // replaced it. With only the first, `--ew-topbar-h` was 0 and the seasonal bar stayed
      // pinned, taking no taps while the header was slim.
      this.topBar = header.querySelector(
        '[data-header-benefits], [data-xb-season]',
      );
      this.navy = header.querySelector('.header__top');
      this.slide = header.querySelector('[data-nav-slide]');
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.state = { slim: false, run: 0, y: Math.max(window.scrollY, 0) };
      this.frame = null;
      this.onScroll = this.onScroll.bind(this);
      this.measure = this.measure.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      if (typeof ResizeObserver === 'function') {
        this.observer = new ResizeObserver(this.measure);
        this.observer.observe(header);
      } else {
        window.addEventListener('resize', this.measure);
      }
      this.measure();
      this.update();
    }

    measure() {
      const root = document.documentElement.style;
      const navy = this.navy ? this.navy.offsetHeight : 0;
      const slide = this.slide ? this.slide.offsetHeight : 0;
      root.setProperty(
        '--ew-topbar-h',
        `${this.topBar ? this.topBar.offsetHeight : 0}px`,
      );
      root.setProperty('--header-stuck-height', `${navy + slide}px`);
      root.setProperty(
        '--header-height',
        `${this.state.slim ? navy : navy + slide}px`,
      );
    }

    onScroll() {
      if (this.frame !== null) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const next = slimStep(
        this.state,
        Math.max(window.scrollY, 0),
        this.desktop.matches,
      );
      const changed = next.slim !== this.state.slim;
      this.state = next;
      if (!changed) return;
      this.wrapper?.classList.toggle('is-slim', next.slim);
      this.measure();
      this.onSlim?.(next.slim);
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.measure);
      this.observer?.disconnect();
      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
      this.wrapper?.classList.remove('is-slim');
      ['--ew-topbar-h', '--header-height', '--header-stuck-height'].forEach(
        (name) => document.documentElement.style.removeProperty(name),
      );
    }
  }

  /* ------------------------------------------------------------ HeaderController */

  class HeaderController {
    constructor(header) {
      this.header = header;
      this.navigation = header.querySelector('[data-header-navigation]');
      // The desktop row and its panel live from `lg` (TN-13, D-69); below it the phone header
      // (Menu, drawer, chips) takes over, 750–989 included.
      this.navigationBreakpoint = window.matchMedia(DESKTOP_QUERY);

      this.handleNavigationBreakpoint =
        this.handleNavigationBreakpoint.bind(this);
      this.handleOpenerClick = this.handleOpenerClick.bind(this);

      this.init();
    }

    init() {
      if (!this.navigation) return;

      this.cacheElements();
      this.desktopNav = new DesktopNav(this.header, this.navigation, this);
      this.initDrawer();
      this.slidingHeader = new SlidingHeader(this.header, (slim) =>
        this.desktopNav?.setBlocked(slim),
      );

      this.navigationBreakpoint.addEventListener(
        'change',
        this.handleNavigationBreakpoint,
      );
    }

    cacheElements() {
      const sectionId = this.header.dataset.sectionId;
      // The drawer is rendered after </header>, so it is looked up by id.
      this.mobileMenu = document.getElementById(`nav-drawer-${sectionId}`);
      this.drawerOpeners = [
        ...this.header.querySelectorAll('[data-nav-drawer-open]'),
      ];
      this.chipsRow = this.header.querySelector('[data-nav-chips]');

      this.overlay = document.getElementById(`header-overlay-${sectionId}`);
    }

    toggleOverlay(isVisible) {
      this.overlay?.classList.toggle('hidden', !isVisible);
    }

    initDrawer() {
      if (this.mobileMenu) this.drawer = new NavDrawer(this.mobileMenu, this);
      this.drawerOpeners.forEach((opener) =>
        opener.addEventListener('click', this.handleOpenerClick),
      );
      if (this.chipsRow) this.chipsPeek = new ChipsPeek(this.chipsRow);
    }

    handleOpenerClick(event) {
      this.drawer?.open(event.currentTarget);
    }

    isMobileMenuOpen() {
      return !!this.drawer?.isOpen();
    }

    closeMobileMenu() {
      this.drawer?.close();
    }

    warmPanelImages(panel) {
      if (!panel) return;

      // The menu cards are `loading="lazy"` so they cost nothing on page load — 62 thumbnails
      // for a closed dropdown used to fire on every page, competing with the hero for
      // connections. But a lazy image inside a `display:none` panel doesn't reliably start
      // fetching the moment the panel is shown: whether the browser re-evaluates in time is a
      // race with layout, and the panel can paint with empty squares. Flipping `loading` to
      // eager forces the fetch to start now. Called on open — and hover opens the desktop
      // panel, so on a pointer device the images are usually in flight before it paints.
      panel.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
    }

    // The API theme.js (Search) uses: close any panel, and which trigger is open.
    closeDesktopMenus() {
      this.desktopNav?.close();
    }

    get activeDesktopItem() {
      return this.desktopNav?.activeTrigger() || null;
    }

    handleNavigationBreakpoint() {
      this.closeDesktopMenus();
    }

    destroy() {
      this.desktopNav?.destroy();
      this.drawer?.destroy();
      this.chipsPeek?.destroy();
      this.drawerOpeners.forEach((opener) =>
        opener.removeEventListener('click', this.handleOpenerClick),
      );

      this.navigationBreakpoint.removeEventListener(
        'change',
        this.handleNavigationBreakpoint,
      );
      this.slidingHeader?.destroy();
    }
  }

  const headerControllers = new Map();

  function initializeHeaders(root = document) {
    const headers = [];

    if (root.matches?.('[data-header]')) {
      headers.push(root);
    }

    headers.push(...(root.querySelectorAll?.('[data-header]') ?? []));

    headers.forEach((header) => {
      if (headerControllers.has(header)) return;

      const controller = new HeaderController(header);
      headerControllers.set(header, controller);

      // Store first header controller globally for cross-component communication
      if (!window.headerController) {
        window.headerController = controller;
      }
    });
  }

  function destroyHeader(header) {
    const controller = headerControllers.get(header);

    if (!controller) return;

    controller.destroy();
    headerControllers.delete(header);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeHeaders(), {
      once: true,
    });
  } else {
    initializeHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeHeaders(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const header = event.target.matches?.('[data-header]')
      ? event.target
      : event.target.querySelector?.('[data-header]');

    if (header) {
      destroyHeader(header);
    }
  });

  if (window.__EW_TEST__) {
    window.EwHeaderTest = {
      _test: {
        accordion,
        holdCorrection,
        choosePad,
        navLabel,
        glideAdvance,
        hoverDelay,
        slimStep,
      },
    };
  }

  // In-page anchor links are handled natively, and deliberately have no JavaScript at all.
  //
  // There used to be a delegated click handler here that called `preventDefault()`, measured the
  // header, subtracted its height from the target's position, ran its own `window.scrollTo`, and
  // pushed the hash by hand. Most of that was working around one thing: the header could not
  // animate, so before every jump the handler reached into the controller and tore the sticky
  // classes off it -- setting `isSticky` from the outside and leaving the instance lying about
  // its own state.
  //
  // None of it is needed now. `scroll-behavior: smooth` on `html` already animates the jump,
  // `scroll-padding-top: var(--header-height)` (app.scss, fed by SlidingHeader above) already
  // keeps the target clear of the header, and the browser already updates the hash and the
  // history entry. Anchors that point at another page work too, which the hand-rolled version
  // only managed by accident.
  //
  // Do not reintroduce a scroll handler for anchors. If a target ever lands underneath the
  // header, the fix is `--header-height` or a `scroll-margin-top` on that element -- not JS.
})();
